import { NavLink } from "react-router-dom";
import { BookOpen, CreditCard, LayoutGrid, LogOut, Menu, Settings, Ticket, Users, UsersRound } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Sheet, SheetClose, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const ICONS = { "/dashboard": LayoutGrid, "/volunteers": Users, "/teams": UsersRound, "/gifts": BookOpen, "/settings": Settings, "/billing": CreditCard, "/admin/codes": Ticket };
const ROLE = { owner: "Owner", admin: "Administrator", viewer: "View-only" };

const StudioLogo = () => <Logo variant="studio-dark" className="h-9" />;

const Links = ({ links, prefix, wrap = (x) => x }) => links.map(([to, label]) => {
  const Icon = ICONS[to] || LayoutGrid;
  return wrap(
    <NavLink key={to} to={to} end={to === "/dashboard"} data-testid={`${prefix}-${label.toLowerCase()}`} className="studio-nav-link"><Icon size={18} />{label}</NavLink>, to,
  );
});

const SideContent = ({ church, user, links, onSignOut, prefix, wrap }) => (
  <div className="flex flex-col h-full bg-[#181818] text-white px-5 py-7">
    <StudioLogo />
    <p className="text-[10px] uppercase tracking-[0.22em] text-white/45 mt-8 mb-3 px-1" data-testid="studio-workspace-label">Workspace</p>
    <p className="font-['Archivo'] font-semibold text-white px-1 mb-6 truncate" data-testid="nav-church-name">{church.name}</p>
    <nav className="flex flex-col gap-1">
      <p className="text-[10px] uppercase tracking-[0.22em] text-white/45 mb-1 px-1">Menu</p>
      <Links links={links} prefix={prefix} wrap={wrap} />
    </nav>
    <div className="mt-auto pt-6 border-t border-white/10">
      <div className="flex items-center gap-3 px-1">
        <span className="h-9 w-9 rounded-full bg-[#EFA4C5] text-[#181818] font-['Archivo'] font-bold flex items-center justify-center">{(user.name || user.email)[0].toUpperCase()}</span>
        <div className="min-w-0">
          <p className="text-sm font-medium truncate" data-testid="studio-user-name">{user.name || user.email}</p>
          <p className="text-[11px] uppercase tracking-[0.14em] text-[#B4E500]" data-testid="studio-user-role">{ROLE[church.role] || church.role}</p>
        </div>
      </div>
      <button type="button" onClick={onSignOut} data-testid={prefix === "nav" ? "sign-out-btn" : "mobile-sign-out-btn"} className="studio-nav-link w-full mt-3"><LogOut size={18} />Sign out</button>
    </div>
  </div>
);

export const StudioShell = ({ church, user, links, onSignOut, banner, children }) => (
  <div className="min-h-screen bg-paper lg:pl-64" data-testid="studio-shell">
    <aside className="hidden lg:block fixed inset-y-0 left-0 w-64 z-30" data-testid="studio-sidebar">
      <SideContent church={church} user={user} links={links} onSignOut={onSignOut} prefix="nav" />
    </aside>
    <header className="lg:hidden sticky top-0 z-30 bg-[#181818] h-16 px-5 flex items-center justify-between">
      <StudioLogo />
      <Sheet>
        <SheetTrigger className="text-white h-11 w-11 -mr-2 flex items-center justify-center rounded-lg hover:bg-white/10" data-testid="mobile-menu-btn" aria-label="Open menu"><Menu size={22} /></SheetTrigger>
        <SheetContent side="left" className="p-0 border-0 w-72 bg-[#181818] text-white">
          <SideContent church={church} user={user} links={links} onSignOut={onSignOut} prefix="mobile-nav"
            wrap={(el, key) => <SheetClose asChild key={key}>{el}</SheetClose>} />
        </SheetContent>
      </Sheet>
    </header>
    {banner}
    <main className="studio-main max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 py-10 lg:py-14">{children}</main>
  </div>
);
